import { useState } from "react"
import { useForm } from "@inertiajs/react"
import { BookOpen, Mic, Users } from "lucide-react"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { formatPhone, isValidEmail } from "@/lib/form-utils"

export function ShareYourStoryDialog({ open, onOpenChange }) {
  const [submitted, setSubmitted] = useState(false)
  const [clientErrors, setClientErrors] = useState({})

  const form = useForm({
    form_name: "share_your_story",
    data: {
      name: "",
      email: "",
      phone: "",
      zip_code: "",
      details: "",
    },
  })

  const validate = () => {
    const errors = {}
    const { name, email, zip_code } = form.data.data

    if (!name.trim()) errors.name = "Name is required"
    if (!email.trim()) {
      errors.email = "Email is required"
    } else if (!isValidEmail(email)) {
      errors.email = "Please enter a valid email address"
    }
    if (!zip_code.trim()) errors.zip_code = "Zip code is required"

    setClientErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    form.post("/form-submissions", {
      preserveScroll: true,
      onSuccess: () => setSubmitted(true),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setSubmitted(false)
      setClientErrors({})
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    form.setData("data", { ...form.data.data, phone: formatted })
  }

  const setField = (field, value) => {
    form.setData("data", { ...form.data.data, [field]: value })
    if (clientErrors[field]) {
      setClientErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const errorFor = (field) =>
    clientErrors[field] || form.errors[`data.${field}`]

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        {submitted ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-7 h-7 text-green-600" />
            </div>
            <ResponsiveDialogHeader className="text-center">
              <ResponsiveDialogTitle className="text-xl">
                You're In — We'll Be in Touch!
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription className="text-base text-zinc-600 mt-2">
                Thank you for applying to be featured in the KF Collective. A
                coach local to your area will reach out to schedule a short
                interview and help tell your story.
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>
            <ResponsiveDialogFooter className="mt-6 sm:justify-center">
              <ResponsiveDialogClose asChild>
                <Button variant="gradient">Done</Button>
              </ResponsiveDialogClose>
            </ResponsiveDialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle className="font-display text-xl">
                Get Featured in The KF Collective
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription asChild>
                <div className="space-y-3 mt-1">
                  <p className="text-zinc-600">
                    Would you be interested in sharing how you incorporate faith
                    in your business? Fill out the form below and one of our
                    coaches will interview you for a featured article in{" "}
                    <span className="font-semibold text-zinc-800">The Collective</span>.
                  </p>
                  <div className="grid grid-cols-3 gap-3 pt-1">
                    <div className="flex flex-col items-center text-center gap-1.5 p-2 rounded-lg bg-zinc-50">
                      <Mic className="w-4 h-4 text-kf-red" />
                      <span className="text-xs text-zinc-600 leading-tight">
                        Quick coach-led interview
                      </span>
                    </div>
                    <div className="flex flex-col items-center text-center gap-1.5 p-2 rounded-lg bg-zinc-50">
                      <BookOpen className="w-4 h-4 text-kf-red" />
                      <span className="text-xs text-zinc-600 leading-tight">
                        Published in The Collective
                      </span>
                    </div>
                    <div className="flex flex-col items-center text-center gap-1.5 p-2 rounded-lg bg-zinc-50">
                      <Users className="w-4 h-4 text-kf-red" />
                      <span className="text-xs text-zinc-600 leading-tight">
                        Inspire other leaders of faith
                      </span>
                    </div>
                  </div>
                </div>
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>

            <div className="space-y-4 py-4 px-4 sm:px-0">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={form.data.data.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Your full name"
                />
                {errorFor("name") && (
                  <p className="text-sm text-red-600">{errorFor("name")}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.data.data.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="you@example.com"
                />
                {errorFor("email") && (
                  <p className="text-sm text-red-600">{errorFor("email")}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.data.data.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zip_code">Zip / Postal Code *</Label>
                <Input
                  id="zip_code"
                  value={form.data.data.zip_code}
                  onChange={(e) => setField("zip_code", e.target.value)}
                  placeholder="12345"
                />
                {errorFor("zip_code") && (
                  <p className="text-sm text-red-600">
                    {errorFor("zip_code")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="details">Additional Details</Label>
                <Textarea
                  id="details"
                  value={form.data.data.details}
                  onChange={(e) => setField("details", e.target.value)}
                  placeholder="Tell us about your leadership journey and what you'd love to share..."
                  rows={4}
                />
              </div>
            </div>

            <ResponsiveDialogFooter>
              <ResponsiveDialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </ResponsiveDialogClose>
              <Button type="submit" variant="gradient" disabled={form.processing}>
                {form.processing ? "Submitting..." : "Submit"}
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
